import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGroup } from '../../contexts/GroupContext';
import { Check } from 'lucide-react';
import { Button, useToast } from '../../components/common';
import { Face, StagePill } from '../../components/dev/AppFleet';
import { deriveState, describeApp } from '../../utils/appState';
import { factsFor, loadFleetSignals } from '../../utils/fleetFacts';
import api from '../../services/api';
import { copyText } from '../../utils/clipboard';
import { DOCS_PATH, OWNER_SUPPLIES, docsURL, newBuildHandoff, readingBlock, useRemoteFlag } from './handoff';
import useArrival from './useArrival';
import './CreateFlow.css';

/**
 * Start developing — the on-ramp, in the two directions people actually arrive from.
 *
 * The lane used to be identical on day one and on day four hundred: mint a token,
 * install the CLI, paste a prompt. That is the right answer exactly once. PICKING
 * UP an app you already own — bind the directory, mint your own key, get somebody
 * to test against — is real work with real commands, and it lived nowhere in the
 * sidebar: it was a copy-paste block inside one app's Testing tab, which you could
 * only reach by already knowing where it was.
 *
 * So there are two lanes, and the page opens on whichever fits. A developer with
 * four apps is far more likely to be continuing than starting.
 *
 * WHY THE COMMANDS ARE BUILT HERE and not printed by the CLI: every one of them
 * needs a value the developer would otherwise have to go and find — an app id, a
 * token shown once, the API base of a non-default backend. Handing somebody a
 * command with a placeholder in it is handing them a lookup task.
 */

/* Two lanes — New app and Continue — chosen from the frame's Develop page
   column (devmap.js), which reads `?lane=`. Tokens was never a lane: it is step
   one of starting, and has its own page in that column. */

const COPY_FAILED = 'Could not copy. Select the text and copy it by hand.';

function Command({ caption, children, note }) {
  const toast = useToast();
  const text = String(children).trim();
  const copy = async () => {
    if (await copyText(text)) toast.success('Copied.');
    else toast.error(COPY_FAILED);
  };
  return (
    <div className="cflow__cmdwrap">
      {caption && <span className="cflow__caption">{caption}</span>}
      <div className="cflow__cmd">
        <pre>{text}</pre>
        <button type="button" onClick={copy}>Copy</button>
      </div>
      {note && <p className="cflow__note">{note}</p>}
    </div>
  );
}

/** The docs, for a human, in the same place the paste points the assistant. */
function DocsLink({ children = 'Read the docs' }) {
  const navigate = useNavigate();
  return (
    <button type="button" className="cflow__link" onClick={() => navigate(DOCS_PATH)}>
      {children} →
    </button>
  );
}

/**
 * The way to the token LEDGER, from the page that mints.
 *
 * Minting shows a value once and then it is gone, which is correct — but it left
 * a developer with no way to answer "what do I already hold, and is it still
 * good?" without minting another one. /tokens is that list: previews, expiry,
 * last used, Revoke. It is deliberately a link and not a lane (Tokens has its own page).
 */
function TokensLink({ children = 'Your tokens' }) {
  const navigate = useNavigate();
  return (
    <button type="button" className="cflow__link" onClick={() => navigate('/tokens')}>
      {children} →
    </button>
  );
}

/**
 * The whole setup as ONE thing to paste into a coding agent.
 *
 * IT IS THE ONLY PATH NOW, not the convenient one. The steps used to repeat
 * every command in it with their own copy buttons, so the page presented one
 * path twice and left the reader to work out which was authoritative. What
 * follows it says what it DOES, and has nothing to copy.
 *
 * It is a PROMPT, not a script: the agent can run commands, so the commands go
 * inside it. Splitting "what to type" from "what to say" only made sense when a
 * human was doing the typing.
 */
function HandOff({ note, children }) {
  const toast = useToast();
  const text = String(children).trim();
  return (
    <section className="cflow__handoff">
      <div className="cflow__handoff-head">
        <p className="cflow__handoff-note">{note}</p>
        <button
          type="button"
          className="cflow__btn"
          onClick={async () => {
            if (await copyText(text)) toast.success('Copied — paste it into your assistant.');
            else toast.error(COPY_FAILED);
          }}
        >
          Copy
        </button>
      </div>
      <pre className="cflow__handoff-body" data-testid="cflow-handoff">{text}</pre>
    </section>
  );
}

function Step({ n, done, title, who, children }) {
  return (
    <li className="cflow__step" data-done={done || undefined}>
      <span className="cflow__n">{done ? '✓' : n}</span>
      <div className="cflow__body">
        <h3 className="cflow__steptitle">
          {title}
          {who && <span className="cflow__who">{who}</span>}
        </h3>
        {children}
      </div>
    </li>
  );
}

/* ── New ──────────────────────────────────────────────────────────────── */

function NewLane({ groupId, groupName, onRegistered }) {
  const toast = useToast();
  const navigate = useNavigate();
  const remote = useRemoteFlag();
  const [kind, setKind] = useState('app');
  const [token, setToken] = useState(null);       // shown once, never re-fetchable
  const [name, setName] = useState('');
  const [minting, setMinting] = useState(false);
  // Step 3 is why this tab is worth keeping open: it watches for the push.
  const arrived = useArrival(groupId, onRegistered);

  const mint = async () => {
    const label = name.trim();
    if (!label) { toast.error('Give the token a name — you will want to know which machine it is.'); return; }
    setMinting(true);
    try {
      const res = await api.createDeveloperToken(label, 90, groupId || null);
      const value = res.token || res.developer_token || null;
      if (!value) toast.error('Token created, but the value was not returned. Check the Tokens list.');
      setToken(value);
      setName('');
    } catch (err) {
      toast.error(err.message || 'Could not create a token.');
    }
    setMinting(false);
  };

  const handoff = newBuildHandoff({ kind, token, groupId, groupName, remote });

  return (
    <>
      <div className="cflow__fork" role="group" aria-label="What are you building">
        {[['app', 'Application'], ['plugin', 'Plugin']].map(([id, label]) => (
          <button
            key={id}
            type="button"
            className="cflow__forkbtn"
            data-active={kind === id || undefined}
            aria-pressed={kind === id}
            onClick={() => setKind(id)}
          >
            {label}
          </button>
        ))}
        <p className="cflow__forknote">
          {kind === 'app'
            ? 'An app is approved once, then reads and writes through the SDK.'
            : 'A plugin offers tools. Each tool declares what it reads and writes, and is handed only that. No CRUX.'}
        </p>
      </div>

      <ol className="cflow__steps">
        <Step n="1" done={!!token} title="Get your credential" who="here">
          {token ? (
            <>
              <p className="cflow__d">Save this now — it is not shown again.</p>
              <Command>{token}</Command>
              <p className="cflow__aside"><TokensLink>All your tokens</TokensLink></p>
            </>
          ) : (
            <>
              <p className="cflow__d">
                A developer token identifies <strong>you</strong>, never an app's users. It
                authenticates the CLI and the package downloads. One per machine.
              </p>
              <div className="cflow__inline">
                <input
                  className="cflow__input"
                  placeholder="Which machine? e.g. MacBook Pro"
                  value={name}
                  maxLength={100}
                  onChange={e => setName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && mint()}
                />
                <button type="button" className="cflow__btn" onClick={mint} disabled={minting || !name.trim()}>
                  {minting ? 'Creating…' : 'Create token'}
                </button>
              </div>
              <p className="cflow__aside">
                Already minted one on this machine? <TokensLink>Check your tokens</TokensLink>
              </p>
            </>
          )}
        </Step>

        {/* THE TOKEN COMES FIRST, THEN THE PASTE IT FILLS. The paste used to
            head the page, so the first thing a new developer met was a block
            with <YOUR_TOKEN> in it and a Copy button that copied the placeholder.
            It sits here now, after the step that makes it whole, and is the
            only thing on the page to copy. */}
        <Step n="2" title="Paste it into your assistant" who="the assistant">
          <p className="cflow__d">
            Open an empty folder in VS Code or Cursor, start Claude Code, and paste the
            block below. It installs the CLI, logs in, scaffolds the {kind}, reads the
            docs, and designs it with you.
          </p>
          <HandOff
            note={token
              ? 'Everything, in one paste. Your token is already in it — it is not shown again.'
              : 'Create a token in step 1 and it fills in here. Everything, in one paste.'}
          >
            {handoff}
          </HandOff>
          <p className="cflow__aside">
            This points your assistant at our documentation, so it works from what the
            platform actually does rather than guessing. <DocsLink />
          </p>
          <p className="cflow__aside">
            Needs <a href="https://pipx.pypa.io" target="_blank" rel="noreferrer">pipx</a>{' '}
            (<code>brew install pipx</code>). Nothing else to copy.
          </p>
        </Step>

        <Step n="3" done={!!arrived} title="Watch it arrive" who="here">
          {arrived ? (
            <>
              <p className="cflow__d"><strong>{arrived.name}</strong> is registered.</p>
              <button
                type="button"
                className="cflow__btn"
                onClick={() => navigate(arrived.is_plugin ? `/plugins/${arrived.id}` : `/applications/${arrived.id}`)}
              >
                Open it →
              </button>
            </>
          ) : (
            <p className="cflow__d cflow__waiting">
              Leave this open. When your assistant runs{' '}
              <code>{kind === 'plugin' ? 'paradigm plugin push' : 'paradigm app push'}</code>, the {kind}{' '}
              appears here.
            </p>
          )}
        </Step>
      </ol>

      {/* The form is still there for anyone who wants it — it is simply no longer
          the thing a new developer is asked to choose against a coding agent. */}
      <p className="cflow__aside">
        Rather fill in a form?{' '}
        <button
          type="button"
          className="cflow__link"
          onClick={() => navigate(kind === 'plugin' ? '/plugins/new' : '/developer?tab=get-started&mode=manual&type=app')}
        >
          Register manually
        </button>
      </p>
    </>
  );
}

/* ── Continue ─────────────────────────────────────────────────────────── */

/* A numbered step that says who does it. The number box turns into a check
   once the step is done, and the title goes quiet. */
function ContinueStep({ n, done, title, who, children }) {
  return (
    <li className="cflow__dstep" data-done={done || undefined}>
      <span className="cflow__dstep-n">{done ? <Check size={12} strokeWidth={2.6} aria-label="Done" /> : n}</span>
      <div className="cflow__dstep-main">
        <div className="cflow__dstep-head">
          <h3>{title}</h3>
          <span className="cflow__dstep-who" data-here={who === 'here' || undefined}>
            {who === 'here' ? 'Here' : 'Your assistant'}
          </span>
        </div>
        {children}
      </div>
    </li>
  );
}

function ContinueLane({ apps, groupId, groupName }) {
  const navigate = useNavigate();
  const toast = useToast();
  const remote = useRemoteFlag();
  /* NOTHING IS MINTED UNTIL SOMEBODY ASKS. Opening this page is not a request
     for a credential — most visits are from a machine that is already logged in
     — and a token minted per visit is a ledger nobody can read. The paste carries
     a placeholder until "Create token" is pressed.

     BEING SIGNED INTO THIS PAGE IS NOT BEING LOGGED IN ON THAT MACHINE, though,
     which is why the paste opens with `paradigm login` and not `context set`:
     the latter fails outright against a CLI that has never held a token. */
  const [token, setToken] = useState(null);       // shown once, never re-fetchable
  // A mint that answered without a value: made, but nothing to paste.
  const [valueLost, setValueLost] = useState(false);
  const [machine, setMachine] = useState('');
  const [minting, setMinting] = useState(false);
  const mint = async () => {
    const label = machine.trim();
    if (!label) { toast.error('Give the token a name — you will want to know which machine it is.'); return; }
    setMinting(true);
    try {
      const res = await api.createDeveloperToken(label, 90, groupId || null);
      const value = res.token || res.developer_token || null;
      if (!value) toast.error('Token created, but the value was not returned. Check the Tokens list.');
      setToken(value);
      setValueLost(!value);
      setMachine('');
    } catch (err) {
      toast.error(err.message || 'Could not create a token.');
    }
    setMinting(false);
  };

  const [appId, setAppId] = useState(apps[0]?.id || '');
  const [signals, setSignals] = useState(null);
  const [draft, setDraft] = useState(null);

  const app = apps.find(a => a.id === appId) || apps[0];

  /* Every row's stage, from the same derivation the Apps list uses — so the two
     cannot say different things about the same app. */
  useEffect(() => {
    let cancelled = false;
    loadFleetSignals(groupId).then(s => { if (!cancelled) setSignals(s); }).catch(() => {});
    return () => { cancelled = true; };
  }, [groupId]);

  // The draft is per app, and only the picked one's blocker is shown.
  useEffect(() => {
    if (!app || app.is_plugin) { setDraft(null); return undefined; }
    let cancelled = false;
    api.request(`/third-party/${app.id}/draft`)
      .then(d => { if (!cancelled) setDraft(d); })
      .catch(() => { if (!cancelled) setDraft(null); });
    return () => { cancelled = true; };
  }, [app]);

  if (!app) return null;

  const picked = describeApp(app, factsFor(app, signals, draft));
  const bind = `paradigm app adopt ${app.id}${remote}`;
  const key = 'paradigm app key create --name "$(hostname)"';
  const tester = 'paradigm app test-users create';
  /* THE APP'S OWN OWNERSHIP, not the portal's current context. They can differ —
     you can be looking at a personal app while the portal sits in a group — and
     a command list built from the wrong one silently binds the wrong identity.
     `app.group_id` is the app record's answer and it is the one that counts. */
  const appGroupId = app.group_id || null;
  const contextCmd = appGroupId
    ? `paradigm context set --group-id ${appGroupId}`
    : 'paradigm context set --group-id personal';

  const handoff =
    `This project is the ofSelf ${app.is_plugin ? 'plugin' : 'app'} "${app.name}".\n`
    + `${appGroupId ? `It is owned by the group ${groupName || appGroupId}.` : 'It is my personal app, not a group\'s.'}\n\n`
    + `Set this machine up for it, then tell me where it stands.\n\n`
    + `1. Bind the project and get credentials:\n\n`
    + `  paradigm login --token ${token || '<PASTE A TOKEN, or mint one below>'}${remote}\n`
    + `  ${contextCmd}\n`
    + `  ${bind}\n`
    + `  ${key}\n`
    + `  ${tester}\n\n`
    // THE ONE SDK FACT THAT STAYS IS THE DESTRUCTIVE ONE. Everything else is
    // recoverable by running a command again; regenerating an encryption key
    // over existing data is not, and "go and read the docs" is not good enough
    // for a step that can lose somebody's data.
    + `2. Restore \`.env\`. It is gitignored, so a fresh clone has none — read\n`
    + `   ${docsURL()} for what this project's SDK needs, and ask me for anything\n`
    + `   you cannot derive. Do NOT regenerate an encryption key if data is\n`
    + `   already encrypted under the old one.\n\n`
    + `3. Then run \`paradigm readiness\` and \`paradigm crux check\`, read CRUX.md,\n`
    + `   and help me [what you want to change].\n\n`
    + `Reference, when you need it:\n`
    + `${readingBlock()}\n\n`
    + OWNER_SUPPLIES[app.is_plugin ? 'plugin' : 'app'];

  const copy = async () => {
    if (await copyText(handoff)) toast.success('Copied — paste it into your assistant.');
    else toast.error(COPY_FAILED);
  };

  return (
    <>
      <section className="cflow__working">
        <h2 className="cflow__label" id="cflow-working">Working on</h2>
        <div className="cflow__apps" role="group" aria-labelledby="cflow-working">
          {apps.map(a => {
            const state = deriveState(a, factsFor(a, signals));
            return (
              <button
                key={a.id}
                type="button"
                className="cflow__app"
                data-current={a.id === app.id || undefined}
                aria-pressed={a.id === app.id}
                onClick={() => setAppId(a.id)}
              >
                <Face app={a} />
                <span className="cflow__app-names">
                  <span className="cflow__app-name">{a.name}</span>
                  <span className="cflow__app-sub">
                    {a.is_plugin ? 'Plugin' : 'App'} · v{a.spec_version || 1} · {a.client_id}
                  </span>
                </span>
                <StagePill state={state} />
              </button>
            );
          })}
        </div>
        {picked.blocking && (
          <p className="cflow__aside">
            {app.name}:{' '}
            <button
              type="button"
              className="cflow__link"
              onClick={() => navigate(
                picked.blocking.href || `/applications/${app.id}${picked.blocking.panel ? `?panel=${picked.blocking.panel}` : ''}`,
              )}
            >
              {picked.blocking.text} →
            </button>
          </p>
        )}
      </section>

      <ol className="cflow__dsteps">
        <ContinueStep n="1" title="Paste it into your assistant" who="assistant">
          <p className="cflow__dnote">
            Binds this directory to {app.name}, mints a key for this machine and creates a
            test user. The app id and owner are already in it.
          </p>
          <div className="cflow__paste">
            <pre data-testid="cflow-paste">{handoff}</pre>
            <Button size="sm" className="cflow__paste-copy" onClick={copy}>Copy</Button>
          </div>
        </ContinueStep>

        <ContinueStep n="2" done={!!token} title="Mint a token for this machine" who="here">
          {token ? (
            <p className="cflow__dnote">Done. It is in the paste above and will not be shown again.</p>
          ) : (
            <>
              <p className="cflow__dnote">
                {valueLost
                  ? 'A token was made, but its value did not come back, so the paste still has a placeholder. Mint another, or see your tokens.'
                  : 'Only needed if the CLI has never been logged in here.'}
              </p>
              <div className="cflow__dstep-row">
                <input
                  className="cflow__dinput"
                  aria-label="Which machine?"
                  placeholder="Which machine? e.g. MacBook Pro"
                  value={machine}
                  maxLength={100}
                  onChange={e => setMachine(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && mint()}
                />
                <Button onClick={mint} loading={minting} disabled={!machine.trim()}>
                  {minting ? 'Creating…' : 'Create token'}
                </Button>
              </div>
            </>
          )}
        </ContinueStep>
      </ol>

      <div className="cflow__links">
        <p className="cflow__aside">
          The paste points your assistant at our documentation, so it works from what the
          platform actually does rather than guessing. <DocsLink />
        </p>
        {/* "Has this machine ever been logged in?" is answered by the LIST —
            without this link the only way to check was to mint another. */}
        <p className="cflow__aside"><TokensLink>All your tokens</TokensLink></p>
      </div>

      {/* THE PROSE IS WORTH KEEPING, folded. Each line explains something the
          command cannot — that rebinding DROPS the previous app's credentials,
          that you mint your own key rather than being sent one, that a test user
          has already authorized you. */}
      <details className="cflow__details">
        <summary>What the paste does</summary>
        <ol className="cflow__does">
          <li>
            <strong>Binds this directory to the app.</strong> Rebinding <em>drops</em> the
            previous app's credentials rather than leaving them under the new id — a
            directory carrying one app's id and another's key fails much later as
            <em> Invalid API key</em>, which sends you hunting an expired key instead of a
            stale binding.
          </li>
          <li>
            <strong>Mints your own key.</strong> Nobody has to send you a secret: being
            allowed to see this app is being allowed to manage it. One key per machine —
            every other machine keeps working. It is written straight to
            <code> .paradigm/secrets.toml</code>.
          </li>
          <li>
            <strong>Creates someone to build against.</strong> A sandbox user who has{' '}
            <em>already</em> authorized you — no OAuth round-trip, no callback, no waiting
            for a real person to say yes.
          </li>
          <li>
            <strong>Points your assistant at the docs and at this app's own state</strong>{' '}
            (<code>paradigm readiness</code>, <code>paradigm crux check</code>), so it starts
            from what is true rather than interviewing you cold.
          </li>
        </ol>
        <p className="cflow__aside">
          Credentials for this app live on its{' '}
          <button type="button" className="cflow__link" onClick={() => navigate(`/applications/${app.id}?panel=keys`)}>
            Keys
          </button>{' '}and{' '}
          <button type="button" className="cflow__link" onClick={() => navigate(`/applications/${app.id}?panel=testusers`)}>
            Test users
          </button>{' '}panels.
        </p>
      </details>
    </>
  );
}

/* ── the host ─────────────────────────────────────────────────────────── */

export default function CreateFlow() {
  const { activeGroupId, activeGroup } = useGroup();
  const location = useLocation();
  const navigate = useNavigate();
  const [apps, setApps] = useState(null);         // null = not loaded yet
  const [lane, setLane] = useState(null);         // null until we know which fits
  const urlLane = new URLSearchParams(location.search).get('lane');

  const load = useCallback(async () => {
    try {
      const res = await api.request(`/third-party/my-apps${activeGroupId ? `?group_id=${activeGroupId}` : ''}`);
      setApps(res?.apps || []);
    } catch {
      setApps([]);
    }
  }, [activeGroupId]);

  useEffect(() => { load(); }, [load]);

  /* The lane is chosen ONCE, from what the developer has — and never re-chosen
     underneath them. Without the ref, the New lane's poll finding a freshly
     registered app would flip the page to Continue mid-sentence. */
  const settled = useRef(false);
  useEffect(() => {
    if (urlLane === 'new' || urlLane === 'continue') { setLane(urlLane); settled.current = true; return; }
    if (apps === null || settled.current) return;
    settled.current = true;
    // Continue needs something registered to pick up; an app still being
    // designed (no client id) is not one. The choice is written into the URL so
    // the page column lights the lane this page actually shows.
    const chosen = apps.some(a => a.client_id) ? 'continue' : 'new';
    setLane(chosen);
    navigate(`/create?lane=${chosen}`, { replace: true });
  }, [apps, urlLane, navigate]);

  const usable = (apps || []).filter(a => a.client_id);

  return (
    <div className="cflow">
      <header className="cflow__head">
        <h1 className="cflow__title">{lane === 'continue' ? 'Continue an app' : 'New app'}</h1>
        <p className="cflow__sub">
          {lane === 'continue'
            ? 'Everything you need to work on an app you already own, from a machine that has never seen it.'
            : 'From nothing to an app reading and writing a real identity graph.'}
        </p>
      </header>

      <div className="cflow__body">
        {lane === 'new' && <NewLane groupId={activeGroupId} groupName={activeGroup?.name} onRegistered={load} />}
        {lane === 'continue' && usable.length > 0 && (
          <ContinueLane apps={usable} groupId={activeGroupId} groupName={activeGroup?.name} />
        )}
        {/* Continue with nothing to continue was a blank page once the lane
            tabs (which disabled it) moved to the page column. It says why. */}
        {lane === 'continue' && apps !== null && usable.length === 0 && (
          <div className="cflow__empty">
            <p>Nothing registered yet, so there is no app here to pick up.</p>
            <button type="button" className="cflow__empty-go" onClick={() => navigate('/create?lane=new')}>
              Start a new app →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
